import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import axios from 'axios'
import {apiUrl} from '../../../../api-utils/constantVariables'

const schema = Yup.object().shape({
  otp: Yup.string().max(12, 'Too long'),
  email_otp: Yup.string().max(10, 'Too long'),
})

export function TwoFactorLogin() {
  const navigate = useNavigate()
  const location = useLocation()
  const login_token = (location.state as {login_token?: string})?.login_token
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [sendingEmail, setSendingEmail] = useState(false)

  const formik = useFormik({
    initialValues: {otp: '', email_otp: ''},
    validationSchema: schema,
    onSubmit: async (values) => {
      setError('')
      if (!login_token) {
        setError('Missing login session. Go back to sign in.')
        return
      }
      try {
        const res = await axios.post(`${apiUrl}/admin/auth/login/2fa`, {
          login_token,
          otp: values.otp?.trim() || undefined,
          email_otp: values.email_otp?.trim() || undefined,
        })
        const payload = res.data?.data
        if (payload?.token) {
          const admin = {
            ...(payload.admin || {}),
            JWToken: payload.token,
            must_change_password: !!payload.admin?.must_change_password,
          }
          localStorage.setItem('admin', JSON.stringify(admin))
          window.dispatchEvent(new Event('shrido-admin-profile-changed'))
          if (admin.must_change_password) {
            navigate('/account/settings')
          } else {
            navigate('/dashboard')
          }
        } else {
          setError('Unexpected response')
        }
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.response?.data?.error || 'Invalid code.'
        setError(typeof msg === 'string' ? msg : 'Invalid code.')
      }
    },
  })

  const sendEmailCode = async () => {
    if (!login_token) return
    setSendingEmail(true)
    setInfo('')
    setError('')
    try {
      await axios.post(`${apiUrl}/admin/auth/2fa/email-otp`, {login_token})
      setInfo('If this account exists, an email with a code was sent.')
    } catch {
      setInfo('If this account exists, an email with a code was sent.')
    } finally {
      setSendingEmail(false)
    }
  }

  if (!login_token) {
    return (
      <div className='text-center'>
        <p className='mb-5'>Your two-factor session expired.</p>
        <Link to='/auth/login' className='btn btn-primary'>
          Back to sign in
        </Link>
      </div>
    )
  }

  return (
    <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
      <div className='text-center mb-10'>
        <h1 className='text-dark fw-bolder mb-3'>Two-factor authentication</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          Enter the code from your authenticator app, or use email verification.
        </div>
      </div>
      {error ? <div className='alert alert-danger mb-5'>{error}</div> : null}
      {info ? <div className='alert alert-info mb-5'>{info}</div> : null}

      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>Authenticator code</label>
        <input
          {...formik.getFieldProps('otp')}
          className={clsx('form-control bg-transparent')}
          placeholder='6-digit code'
          autoComplete='one-time-code'
        />
      </div>

      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>Email code (optional)</label>
        <input
          {...formik.getFieldProps('email_otp')}
          className={clsx('form-control bg-transparent')}
          placeholder='If you requested email code'
          autoComplete='one-time-code'
        />
      </div>

      <div className='d-grid gap-3 mb-8'>
        <button type='submit' className='btn btn-primary' disabled={formik.isSubmitting}>
          Verify and continue
        </button>
        <button
          type='button'
          className='btn btn-light'
          onClick={sendEmailCode}
          disabled={sendingEmail}
        >
          {sendingEmail ? 'Sending…' : 'Email me a code'}
        </button>
        <Link to='/auth/login' className='btn btn-link'>
          Cancel
        </Link>
      </div>
    </form>
  )
}
